<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Blood Group Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Filters and Search Button Row 1 -->
                <v-row class="py-1">
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.blood_group_id"
                      :items="bloodGroups"
                      item-title="name"
                      item-value="id"
                      label="Blood Group"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-blood-bag"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.department_id"
                      :items="departments"
                      item-title="name"
                      item-value="id"
                      label="Department"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-office-building"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-select
                      v-model="filters.designation_id"
                      :items="designations"
                      item-title="name"
                      item-value="id"
                      label="Designation"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      prepend-inner-icon="mdi-badge-account"
                    ></v-select>
                  </v-col>
                  <v-col md="3" cols="12">
                    <div class="d-flex justify-start align-center">
                      <v-btn 
                        class="bg-brand w-15" 
                        type="submit" 
                        ref="searchButton"
                        @click="loadReport"
                      >
                        Search
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Blood Group List Expansion Panel -->
                <v-expansion-panels v-model="panel">
                  <v-expansion-panel>
                    <v-expansion-panel-title
                      color="blue-grey-lighten-4"
                      class="panel-header"
                    >
                      Blood Group List
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <!-- Search Form and Export Buttons Row 2 -->
                      <v-row class="py-1">
                        <v-col md="3" cols="12">
                          <v-text-field
                            v-model="search"
                            append-inner-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            density="compact"
                            variant="outlined"
                            bg-color="grey-lighten-4"
                            clearable
                          ></v-text-field>
                        </v-col>
                        <v-col md="6" cols="12">
                          <div class="d-flex justify-center">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          :disabled="!flattenedEmployees.length"
                        >CSV</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          :disabled="!flattenedEmployees.length"
                        >EXCEL</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          :disabled="!flattenedEmployees.length"
                        >PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Data Table -->
                <v-card class="overflow-x-auto">
                  <v-data-table
                    :headers="headers"
                    :items="filteredEmployees"
                    :loading="loading"
                    v-model:items-per-page="itemsPerPage"
                    density="compact"
                    class="min-w-[1200px]"
                  >
                    <template v-slot:item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template v-slot:item.employee_name="{ item }">
                      <div class="d-flex align-left justify-left">
                        <v-avatar size="32" class="mr-3">
                          <v-img v-if="item.image_thumb" :src="getImageUrl(item.image_thumb)" />
                          <v-icon v-else>mdi-account</v-icon>
                        </v-avatar>
                        <div class="text-left">
                          <div class="font-weight-medium">{{ item.name }}</div>
                          <div class="text-caption text-grey">{{ item.name_bangla }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.employee_code="{ item }">
                      <div class="text-left">
                        <span class="font-weight-medium">{{ item.code }}</span>
                      </div>
                    </template>

                    <template v-slot:item.blood_group="{ item }">
                      <div class="d-flex justify-center">
                        <v-chip 
                          size="small" 
                          :color="getBloodGroupColor(item.blood_group?.name)" 
                          variant="tonal"
                        >
                          {{ item.blood_group?.name || '-' }}
                        </v-chip>
                      </div>
                    </template>

                    <template v-slot:item.department="{ item }">
                      <div class="text-left">
                        <span v-if="item.department" class="text-body-2">{{ item.department.name }}</span>
                        <span v-else class="text-grey">-</span>
                      </div>
                    </template>

                    <template v-slot:item.designation="{ item }">
                      <div class="text-left">
                        <span v-if="item.designation" class="text-body-2">{{ item.designation.name }}</span>
                        <span v-else class="text-grey">-</span>
                      </div>
                    </template>

                    <template v-slot:item.employee_type="{ item }">
                      <div class="text-left">
                        <span v-if="item.employee_type" class="text-body-2">{{ item.employee_type.name }}</span>
                        <span v-else class="text-grey">-</span>
                      </div>
                    </template>

                    <template v-slot:item.mobile="{ item }">
                      <div class="d-flex justify-left">
                        <div v-if="item.mobile" class="d-flex align-left">
                          <v-icon size="12" class="mr-1" color="success">mdi-phone</v-icon>
                          <span>{{ item.mobile }}</span>
                        </div>
                        <span v-else class="text-grey">-</span>
                      </div>
                    </template>

                    <template v-slot:item.email="{ item }">
                      <div class="d-flex justify-left">
                        <div v-if="item.email" class="d-flex align-left">
                          <v-icon size="12" class="mr-1" color="info">mdi-email</v-icon>
                          <span class="text-truncate" style="max-width: 150px;">{{ item.email }}</span>
                        </div>
                        <span v-else class="text-grey">-</span>
                      </div>
                    </template>

                    <template v-slot:item.joining_date="{ item }">
                      <div class="text-center">
                        <span class="text-caption">{{ formattedDate(item.joining_date) }}</span>
                      </div>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        @click="viewEmployee(item.id)"
                        icon="mdi-eye"
                        size="small"
                        color="primary"
                        variant="text"
                        title="View Employee"
                      ></v-btn>
                    </template>
                  </v-data-table>
                </v-card>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- No Data Message -->
      <v-row v-if="!loading && (!reportData.blood_groups || reportData.blood_groups.length === 0)">
        <v-col cols="12">
          <v-card>
            <v-card-text class="text-center py-12">
              <v-icon size="80" color="grey-lighten-1">mdi-account-search</v-icon>
              <div class="text-h5 mt-4 mb-2">No employees found</div>
              <div class="text-body-1 text-grey mb-4">Try adjusting your filters or add some employees first.</div>
              <v-btn color="primary" variant="elevated" @click="loadReport">
                <v-icon left>mdi-refresh</v-icon>
                Refresh Data
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import Axios from 'axios'
import { prefix } from '@/store/api'
import formattedDate from '@/plugins/dateFormatter'

const router = useRouter()

// Get Vue instance for export functions
const instance = getCurrentInstance()

// Reactive data
const panel = ref([0])
const search = ref('')
const loading = ref(false)
const itemsPerPage = ref(10)
const reportData = ref({})
const departments = ref([])
const designations = ref([])
const bloodGroups = ref([])

// Filters
const filters = ref({
  department_id: null,
  designation_id: null,
  blood_group_id: null
})

// Table headers
const headers = ref([
  { title: '#SL', key: 'index', sortable: false, align: 'center' },
  { title: 'Employee Name', key: 'employee_name', sortable: true, align: 'center' },
  { title: 'Code', key: 'employee_code', sortable: true, align: 'center' },
  { title: 'Blood Group', key: 'blood_group', sortable: true, align: 'center' },
  { title: 'Mobile', key: 'mobile', sortable: true, align: 'center' },
  { title: 'Email', key: 'email', sortable: true, align: 'center' },
  { title: 'Department', key: 'department', sortable: true, align: 'center' },
  { title: 'Designation', key: 'designation', sortable: true, align: 'center' },
  { title: 'Employee Type', key: 'employee_type', sortable: true, align: 'center' },
  { title: 'Joining Date', key: 'joining_date', sortable: true, align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '80px' }
])

// Computed property to flatten employees from all blood groups
const flattenedEmployees = computed(() => {
  if (!reportData.value.blood_groups) return []
  
  const employees = []
  reportData.value.blood_groups.forEach(group => {
    if (group.employees && group.employees.length > 0) {
      group.employees.forEach(employee => {
        employees.push({
          ...employee,
          blood_group: group.blood_group
        })
      })
    }
  })
  
  return employees
})

// Client-side search across key fields (name, code, department, designation, blood group)
const filteredEmployees = computed(() => {
  const all = flattenedEmployees.value
  const q = search.value.toLowerCase().trim()
  if (!q) return all
  return all.filter(item => (
    (item.name && item.name.toLowerCase().includes(q)) ||
    (item.code && item.code.toLowerCase().includes(q)) ||
    (item.department?.name && item.department.name.toLowerCase().includes(q)) ||
    (item.designation?.name && item.designation.name.toLowerCase().includes(q)) ||
    (item.blood_group?.name && item.blood_group.name.toLowerCase().includes(q)) ||
    (item.mobile && item.mobile.toLowerCase().includes(q)) ||
    (item.email && item.email.toLowerCase().includes(q))

  ))
})

// Methods
const loadReport = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filters.value.department_id) params.append('department_id', filters.value.department_id)
    if (filters.value.designation_id) params.append('designation_id', filters.value.designation_id)
    if (filters.value.blood_group_id) params.append('blood_group_id', filters.value.blood_group_id)

    const response = await Axios.get(`${prefix}hrm/blood-group-report?${params.toString()}`)
    
    if (response.data.success) {
      reportData.value = response.data.data
    }
  } catch (error) {
    console.error('Error loading blood group report:', error)
    console.error('Error details:', error.response?.data)
  } finally {
    loading.value = false
  }
}

const loadFilters = async () => {
  try {
    // Load departments
    const deptResponse = await Axios.get(`${prefix}hrm/get-department-list`)
    if (deptResponse.data.success) {
      departments.value = deptResponse.data.data
    }

    // Load designations
    const desigResponse = await Axios.get(`${prefix}hrm/get-designation-list`)
    if (desigResponse.data.success) {
      designations.value = desigResponse.data.data
    }

    // Load blood groups
    const bgResponse = await Axios.get(`${prefix}hrm/get-blood-group-list`)
    if (bgResponse.data.success) {
      bloodGroups.value = bgResponse.data.data
    }
  } catch (error) {
    console.error('Error loading filter data:', error)
  }
}

const viewEmployee = (employeeId) => {
  router.push(`/hrm/employee-details/${employeeId}`)
}

const resetFilters = () => {
  // Reset all filters
  filters.value = {
    department_id: null,
    designation_id: null,
    blood_group_id: null
  }
  
  // Reset search
  search.value = ''
  
  // Reload report with cleared filters
  loadReport()
}

const exportToCSV = () => {
  const data = [['#', 'Employee Name', 'Code', 'Blood Group', 'Mobile', 'Email', 'Department', 'Designation', 'Employee Type', 'Joining Date']]
  
  flattenedEmployees.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.name,
      item.code,
      item.blood_group?.name || '-',
      item.mobile || '-',
      item.email || '-',
      item.department?.name || '-',
      item.designation?.name || '-',
      item.employee_type?.name || '-',
      formattedDate(item.joining_date)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToCSV(data, "blood_group_report_" + instance.proxy.$fileDate())
}

const exportToExcel = () => {
  const data = [['#', 'Employee Name', 'Code', 'Blood Group', 'Mobile', 'Email', 'Department', 'Designation', 'Employee Type', 'Joining Date']]
  
  flattenedEmployees.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.name,
      item.code,
      item.blood_group?.name || '-',
      item.mobile || '-',
      item.email || '-',
      item.department?.name || '-',
      item.designation?.name || '-',
      item.employee_type?.name || '-',
      formattedDate(item.joining_date)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToExcel(data, "blood_group_report_" + instance.proxy.$fileDate())
}

const exportToPDF = () => {
  let serialNumber = 1
  const columns = [
    "#", 
    "Employee Name", 
    "Code", 
    "Blood Group",
    "Mobile",
    "Email",
    "Department",
    "Designation", 
    "Employee Type",
    "Joining Date"
  ]
  const rows = flattenedEmployees.value.map((item) => [
    serialNumber++,
    item.name,
    item.code,
    item.blood_group?.name || '-',
    item.mobile || '-',
    item.email || '-',
    item.department?.name || '-',
    item.designation?.name || '-',
    item.employee_type?.name || '-',
    formattedDate(item.joining_date)
  ])
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToPDF(columns, rows, "Blood Group Report", "blood_group_report_" + instance.proxy.$fileDate())
}

const getImageUrl = (imagePath) => {
  if (!imagePath) return ''
  return `${prefix.replace('/api/', '')}${imagePath}`
}

const getBloodGroupColor = (bloodGroup) => {
  const colors = {
    'A+': 'red', 'A-': 'red-lighten-2',
    'B+': 'blue', 'B-': 'blue-lighten-2',
    'O+': 'green', 'O-': 'green-lighten-2',
    'AB+': 'purple', 'AB-': 'purple-lighten-2'
  }
  return colors[bloodGroup] || 'grey'
}

// Initialize
onMounted(() => {
  loadFilters()
  loadReport()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.min-w-\[1200px\] {
  min-width: 1200px;
}

/* Custom scrollbar for data table */
.v-data-table ::-webkit-scrollbar {
  height: 8px;
}

.v-data-table ::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.v-data-table ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.v-data-table ::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>